import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { EnvironmentProviders, inject, Provider, provideAppInitializer } from '@angular/core';
import { MatDialogConfig, MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_SELECT_CONFIG } from '@angular/material/select';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from '@angular/material/tooltip';
import { CoreService } from '@portal-client/core/core.service';
import { AuthInterceptorService } from '@portal-core/auth/interceptors/auth-interceptor.service';
import { ProjectDeleterEffect } from '@portal-core/projects/effects/project-deleter.effect';
import { ProjectHydratorEffect } from '@portal-core/projects/effects/project-hydrator.effect';
import { SiteListByBuildEffect } from '@portal-core/sites/effects/site-list-by-build.effect';
import { USER_SETTINGS_SERVICE } from '@portal-core/users/tokens/user-settings-service.token';
import { NAVIGATOR_PROVIDERS } from '@portal-core/util/navigator.provider';
import { WINDOW_PROVIDERS } from '@portal-core/util/window.provider';
import { CookieService } from 'ngx-cookie-service';

/**
 * App-wide root providers for portal-client (formerly CoreModule, consumed via importProvidersFrom).
 * Spread into the bootstrapApplication providers in main.ts. The NGXS store/state and
 * auth/current/layout service configuration live in main.ts directly; this carries the remaining
 * non-NGXS app-wide providers (HttpClient + auth interceptor, Material default options, browser
 * helpers, and the eager startup effects).
 */
export function provideCore(): (Provider | EnvironmentProviders)[] {
  return [
    CookieService,
    ...NAVIGATOR_PROVIDERS,
    ...WINDOW_PROVIDERS,
    provideHttpClient(withInterceptorsFromDi()),
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptorService, multi: true },
    { provide: USER_SETTINGS_SERVICE, useExisting: CoreService },
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline', subscriptSizing: 'dynamic' } },
    // Material's MDC mat-select renders a checkmark on the selected option by default; the pre-MDC
    // (Material 15) select hid it. Restore that globally so single-selection dropdowns show no check
    // indicator (multi-select, which uses checkboxes, is unaffected).
    { provide: MAT_SELECT_CONFIG, useValue: { hideSingleSelectionIndicator: true } },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS, useFactory: function () {
        return {
          ...new MatDialogConfig(),
          maxHeight: '90vh'
        };
      }
    },
    {
      // Material 21 removed MAT_TOOLTIP_DEFAULT_OPTIONS_FACTORY, so inline the base MatTooltipDefaultOptions
      // defaults it used to return before applying our disableTooltipInteractivity override.
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useFactory: function () {
        return {
          showDelay: 0,
          hideDelay: 0,
          touchendHideDelay: 1500,
          touchGestures: 'auto',
          position: 'below',
          positionAtOrigin: false,
          disableTooltipInteractivity: true
        };
      }
    },
    {
      provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: {
        panelClass: 'mc-snack-bar'
      }
    },
    provideAppInitializer(() => {
      // Eagerly instantiate these effects at startup so their subscriptions are wired up
      // (previously an APP_INITIALIZER no-op factory with a deps array).
      inject(ProjectHydratorEffect);
      inject(ProjectDeleterEffect);
      inject(SiteListByBuildEffect);
    })
  ];
}
